/**
 * native-ir-widgets a2ui catalog — the frontend-owned metadata the converter +
 * runtime need (component names, aliases, kinds, typed prop sets). Derived from
 * `backend/widget-native/a2ui/native-ir-widgets.catalog.json` (generated from the
 * ONE registry SSOT). Structural validation only: typed components validate
 * their prop keys; untyped customs pass props through opaquely.
 *
 * A stricter, schema-complete validation is available by injecting the full
 * catalog JSON into `validateA2uiComponents` (the CLI + tests read it from disk);
 * this bundled meta keeps the browser converter/runtime dependency-free.
 */

export const WIDGETS_CATALOG_ID = 'https://viagent.collov.ai/a2ui/v1.0/native-ir-widgets.json';
export const WIDGET_PROTOCOL_VERSION = 'v1.0';
export const WIDGET_APP_MIN_VERSION = '0.10.0';

/** component → { aliases, regid (viewIr type), kind, typed, props:Set } */
export const CATALOG_META = {
  Text: { regid: 'Text', kind: 'core-extension', typed: true, aliases: [], props: ['align', 'component', 'maxLines', 'preserveNewlines', 'region', 'text', 'tone', 'value', 'variant', 'visible', 'weight'] },
  Button: { regid: 'Button', kind: 'core-extension', typed: true, aliases: [], props: ['action', 'block', 'checks', 'child', 'component', 'enabled', 'icon', 'label', 'loading', 'region', 'size', 'variant', 'visible', 'weight'] },
  Icon: { regid: 'Icon', kind: 'core-extension', typed: true, aliases: [], props: ['component', 'name', 'region', 'size', 'tone', 'visible', 'weight'] },
  Image: { regid: 'Image', kind: 'core-extension', typed: true, aliases: [], props: ['alt', 'component', 'description', 'fallback', 'fallbackColor', 'fallbackKind', 'fallbackShape', 'fallbackTone', 'fallbackValue', 'fit', 'inset', 'lift', 'plate', 'position', 'ratio', 'region', 'rounded', 'shape', 'src', 'url', 'variant', 'visible', 'weight'] },
  Video: { regid: 'Video', kind: 'core-extension', typed: true, aliases: [], props: ['autoplay', 'component', 'controls', 'fit', 'label', 'loop', 'muted', 'poster', 'posterUrl', 'ratio', 'region', 'src', 'url', 'visible', 'weight'] },
  Divider: { regid: 'Divider', kind: 'core-extension', typed: true, aliases: [], props: ['axis', 'component', 'region', 'variant', 'visible', 'weight'] },
  Column: { regid: 'Vertical', kind: 'core-extension', typed: true, aliases: ['Vertical'], props: ['align', 'children', 'component', 'divided', 'gap', 'justify', 'region', 'visible', 'weight'] },
  Row: { regid: 'Horizontal', kind: 'core-extension', typed: true, aliases: ['Horizontal'], props: ['align', 'children', 'component', 'gap', 'justify', 'region', 'visible', 'weight', 'wrap'] },
  Panel: { regid: 'Surface', kind: 'panel', typed: true, aliases: ['Surface'], props: ['children', 'component', 'padding', 'radius', 'region', 'tone', 'variant', 'visible', 'weight'] },
  Card: { regid: 'Card', kind: 'core-extension', typed: true, aliases: [], props: ['aspect', 'badges', 'component', 'eyebrow', 'eyebrowTone', 'fit', 'layout', 'media', 'mediaWidth', 'meta', 'motionSrc', 'overlayAlign', 'region', 'scrim', 'subtitle', 'surface', 'title', 'visible', 'weight'] },
  // a2ui core-18 registered with our kit (basic catalog props verbatim, kind core)
  List: { regid: 'List', kind: 'core', typed: true, aliases: [], props: ['align', 'children', 'component', 'direction', 'region', 'visible', 'weight'] },
  Modal: { regid: 'Modal', kind: 'core', typed: true, aliases: [], props: ['component', 'content', 'region', 'trigger', 'visible', 'weight'] },
  Tabs: { regid: 'Tabs', kind: 'core', typed: true, aliases: [], props: ['component', 'region', 'tabs', 'visible', 'weight'] },
  AudioPlayer: { regid: 'AudioPlayer', kind: 'core', typed: true, aliases: [], props: ['component', 'description', 'region', 'url', 'visible', 'weight'] },
  CheckBox: { regid: 'CheckBox', kind: 'core', typed: true, aliases: [], props: ['component', 'label', 'region', 'value', 'visible', 'weight'] },
  Slider: { regid: 'Slider', kind: 'core', typed: true, aliases: [], props: ['component', 'label', 'max', 'min', 'region', 'steps', 'value', 'visible', 'weight'] },
  TextField: { regid: 'TextField', kind: 'core', typed: true, aliases: [], props: ['component', 'label', 'placeholder', 'region', 'value', 'variant', 'visible', 'weight'] },
  ChoicePicker: { regid: 'ChoicePicker', kind: 'core', typed: true, aliases: [], props: ['component', 'displayStyle', 'filterable', 'label', 'options', 'region', 'value', 'variant', 'visible', 'weight'] },
  DateTimeInput: { regid: 'DateTimeInput', kind: 'core', typed: true, aliases: [], props: ['component', 'enableDate', 'enableTime', 'label', 'max', 'min', 'region', 'value', 'visible', 'weight'] },
  // untyped customs / composed — props pass through opaquely
  Badge: untyped('Badge'), BarChart: untyped('BarChart'), BeforeAfterCompare: untyped('BeforeAfterCompare'),
  CaloryRing: untyped('CaloryRing', 'composed'), ChatComposer: untyped('ChatComposer'),
  ChatPage: untyped('ChatPage'), ChatThread: untyped('ChatThread'), CaptureButton: untyped('CaptureButton'), Carousel: untyped('Carousel'),
  ConnectorLayer: untyped('ConnectorLayer'), Disclosure: untyped('Disclosure'), EmptyState: untyped('EmptyState'),
  ErrorState: untyped('ErrorState'), Gauge: untyped('Gauge'), Grid: untyped('Grid'), ItemCalendar: untyped('ItemCalendar'),
  ItemCollection: untyped('ItemCollection'), ItemPage: untyped('ItemPage'), KeyValueList: untyped('KeyValueList'),
  MediaPicker: untyped('MediaPicker'), MessageBubble: untyped('MessageBubble'),
  ModeSwitch: untyped('ModeSwitch'), Monogram: untyped('Monogram'),
  NavList: untyped('NavList'), CheckList: untyped('CheckList'), NoticeList: untyped('NoticeList'), NumberField: untyped('NumberField'), OptionSelect: untyped('OptionSelect'),
  Overlay: untyped('Overlay'), PageShell: untyped('PageShell'), PendingCard: untyped('PendingCard'),
  RoleMediaForm: untyped('RoleMediaForm'), ScanEditor: untyped('ScanEditor'), SearchField: untyped('SearchField'),
  SectionHeader: untyped('SectionHeader'), SessionCollection: untyped('SessionCollection'), SessionPage: untyped('SessionPage'),
  Sheet: untyped('Sheet'), SlotBoard: untyped('SlotBoard'), Stack: untyped('Stack'), StatTileRow: untyped('StatTileRow'),
  TextBlock: untyped('TextBlock'), TextComposer: untyped('TextComposer'), Timeline: untyped('Timeline'),
  ToolGrid: untyped('ToolGrid'), WeekReview: untyped('WeekReview', 'composed'), host: untyped('host'),
  // Widget-only native cards; entries mirror the generated widget catalog.
  ActivityShelf: untyped('ActivityShelf'),
  AnswerShelf: untyped('AnswerShelf'),
  CategoryShelf: untyped('CategoryShelf'),
  CountsCard: untyped('CountsCard'),
  DayBars: untyped('DayBars'),
  DayCells: untyped('DayCells'),
  DeckCard: untyped('DeckCard'),
  DeliveryCard: untyped('DeliveryCard'),
  MeterCard: untyped('MeterCard'),
  NotePanel: untyped('NotePanel'),
  Padding: untyped('Padding'),
  ProposalCard: untyped('ProposalCard'),
  StatHeadline: untyped('StatHeadline'),
  StatusPanel: untyped('StatusPanel'),
  TallyCard: untyped('TallyCard'),
  ThoughtCard: untyped('ThoughtCard'),
  ThumbnailShelf: untyped('ThumbnailShelf'),
  WishlistShelf: untyped('WishlistShelf'),
};

function untyped(regid, kind = 'custom') {
  return { regid, kind, typed: false, aliases: [], props: [] };
}

/** viewIr registry type → canonical a2ui component (Vertical→Column, Surface→Panel …). */
export const VIEWIR_TO_A2UI = (() => {
  const m = {};
  for (const [name, meta] of Object.entries(CATALOG_META)) {
    m[meta.regid] = name;
    for (const a of meta.aliases) m[a] = name;
  }
  return m;
})();

/** a2ui component (or one of its aliases) → canonical component name, or null. */
export function canonicalComponent(name) {
  if (CATALOG_META[name]) return name;
  for (const [canon, meta] of Object.entries(CATALOG_META)) {
    if (meta.aliases.includes(name)) return canon;
  }
  return null;
}

/** canonical a2ui component → the viewIr KIT type the wrappers render it as. */
export function a2uiToViewIrType(name) {
  const canon = canonicalComponent(name);
  return canon ? CATALOG_META[canon].regid : name;
}

/**
 * Structural validation of one converted a2ui component node against the bundled
 * meta. Returns an array of error strings (empty = valid). An unknown component
 * or (for a TYPED component) an unknown prop fails loudly. `fullCatalog` (the
 * parsed catalog JSON) tightens typed-prop validation when provided.
 */
export function validateComponentNode(node, fullCatalog = null) {
  const errors = [];
  const name = node && node.component;
  if (!name || typeof name !== 'string') { errors.push('component node missing "component"'); return errors; }
  const canon = canonicalComponent(name);
  if (!canon) { errors.push(`unknown component "${name}"`); return errors; }
  const meta = CATALOG_META[canon];
  if (!meta.typed) return errors; // untyped custom — props opaque

  const allowed = new Set(meta.props);
  // extra structural props the converter emits on any component
  ['id', 'children', 'metadata', 'key'].forEach((k) => allowed.add(k));
  // on_<signal> ActionLists are converter-emitted behavior props
  const schemaProps = fullCatalog?.components?.[canon]?.properties
    ? new Set(Object.keys(fullCatalog.components[canon].properties))
    : null;
  for (const k of Object.keys(node)) {
    if (k === 'component') continue;
    if (/^on_[A-Za-z0-9_]+$/.test(k)) continue;
    const known = schemaProps ? (schemaProps.has(k) || allowed.has(k)) : allowed.has(k);
    if (!known) errors.push(`component "${name}" has unknown prop "${k}"`);
  }
  return errors;
}

// ── widgets-formalize target model (SSOT: backend/widget-native/registry.json
//    `a2ui.target`; `gen_a2ui_catalog.py --report-formalize`) ────────────────
//
// Every native-widget component resolves to ONE a2ui target: a pure CORE host
// (drop the custom), a CORE host + a `collov_*` extension (our extra semantics
// as an a2ui-sanctioned extension, never a prop superset), or a true CUSTOM
// (no core semantics). Keyed by the CANONICAL a2ui component name (Vertical→
// Column, Horizontal→Row, Surface→Panel are already canonicalised).
//
// `host` is the core component the target-form converter emits as `component`.
// `extension` (core-extension only) is the `collov_*` key that carries the
// formalised payload; the widget kit re-keys it back to the origin component's
// renderer (parity by construction). A pure-core DROP (host ≠ the origin, no
// extension) carries a `vi_origin` marker instead so our kit still renders the
// familiar form for one release while the WIRE is pure core.
export const A2UI_TARGET = {
  // pure CORE hosts that ARE already core (no drop, no extension)
  Text: { kind: 'core-extension', host: 'Text', extension: 'collov_text' },
  Button: { kind: 'core-extension', host: 'Button', extension: 'collov_button' },
  Icon: { kind: 'core-extension', host: 'Icon', extension: 'collov_icon' },
  Image: { kind: 'core-extension', host: 'Image', extension: 'collov_image' },
  Video: { kind: 'core-extension', host: 'Video', extension: 'collov_video' },
  Divider: { kind: 'core-extension', host: 'Divider', extension: 'collov_divider' },
  Row: { kind: 'core-extension', host: 'Row', extension: 'collov_row' },
  Column: { kind: 'core-extension', host: 'Column', extension: 'collov_column' },
  Card: { kind: 'core-extension', host: 'Card', extension: 'collov_card' },
  Badge: { kind: 'core-extension', host: 'Text', extension: 'collov_badge' },
  Monogram: { kind: 'core-extension', host: 'Text', extension: 'collov_monogram' },
  BeforeAfterCompare: { kind: 'core-extension', host: 'Row', extension: 'collov_before_after' },
  Carousel: { kind: 'core-extension', host: 'List', extension: 'collov_carousel' },
  Disclosure: { kind: 'core-extension', host: 'Card', extension: 'collov_disclosure' },
  ItemCalendar: { kind: 'core-extension', host: 'List', extension: 'collov_item_calendar' },
  ItemCollection: { kind: 'core-extension', host: 'List', extension: 'collov_item_collection' },
  SessionCollection: { kind: 'core-extension', host: 'List', extension: 'collov_session_collection' },
  Timeline: { kind: 'core-extension', host: 'List', extension: 'collov_timeline' },
  ToolGrid: { kind: 'core-extension', host: 'List', extension: 'collov_tool_grid' },
  Sheet: { kind: 'core-extension', host: 'Modal', extension: 'collov_sheet' },
  NumberField: { kind: 'core-extension', host: 'TextField', extension: 'collov_number_field' },
  SearchField: { kind: 'core-extension', host: 'TextField', extension: 'collov_search_field' },
  TextComposer: { kind: 'core-extension', host: 'TextField', extension: 'collov_text_composer' },
  // pure CORE — drop the custom, emit a core host (a `vi_origin` marker keeps our
  // kit rendering the familiar form for one release)
  SectionHeader: { kind: 'core', host: 'Text' },
  TextBlock: { kind: 'core', host: 'Text' },
  KeyValueList: { kind: 'core', host: 'List' },
  NoticeList: { kind: 'core', host: 'List' },
  StatTileRow: { kind: 'core', host: 'Row' },
  EmptyState: { kind: 'core', host: 'Card' },
  ErrorState: { kind: 'core', host: 'Card' },
  PendingCard: { kind: 'core', host: 'Card' },
  ModeSwitch: { kind: 'core', host: 'Tabs' },
  OptionSelect: { kind: 'core', host: 'ChoicePicker' },
  Padding: { kind: 'core', host: 'Column' },
  // core-18 components that are ALREADY core (self host, no transform)
  List: { kind: 'core', host: 'List' }, Modal: { kind: 'core', host: 'Modal' },
  Tabs: { kind: 'core', host: 'Tabs' }, AudioPlayer: { kind: 'core', host: 'AudioPlayer' },
  CheckBox: { kind: 'core', host: 'CheckBox' }, Slider: { kind: 'core', host: 'Slider' },
  TextField: { kind: 'core', host: 'TextField' }, ChoicePicker: { kind: 'core', host: 'ChoicePicker' },
  DateTimeInput: { kind: 'core', host: 'DateTimeInput' },
};

/** The 16 true customs (no core semantics — the `component` id stays). */
export const CUSTOM_TARGETS = new Set([
  'BarChart', 'CaptureButton', 'ConnectorLayer', 'Gauge', 'Grid', 'ItemPage',
  'MediaPicker', 'Overlay', 'PageShell', 'RoleMediaForm', 'ScanEditor',
  'SessionPage', 'SlotBoard', 'Stack', 'Panel', 'host',
]);

/** collov_* extension key → the origin a2ui component the widget kit re-keys to. */
export const EXTENSION_TO_COMPONENT = (() => {
  const m = {};
  for (const [name, t] of Object.entries(A2UI_TARGET)) if (t.extension) m[t.extension] = name;
  return m;
})();

/**
 * The formalize target for a component (by any name/alias). Unknown or explicitly
 * custom → `{ kind: 'custom', host: <canon> }` (the `component` id is unchanged).
 */
export function targetFor(name) {
  const canon = canonicalComponent(name) || name;
  if (CUSTOM_TARGETS.has(canon)) return { kind: 'custom', host: canon };
  return A2UI_TARGET[canon] || { kind: 'custom', host: canon };
}
